import { supportsRequiredApi } from "./powerpointRunner";
import { reconcileUids, type ReconciledSlide, type ReconcileReport } from "./reconcileUids";
import { subscribeSelection } from "./selectionWatcher";

/**
 * 走査結果を 1 箇所で持ち、全画面へ配る。
 *
 * 画面ごとに `PowerPoint.run` を投げると、88 枚の読み取り（実測 150ms）が画面数だけ走る。
 * 走査は識別子の発行と振り直しを伴うので、なおさら重ねたくない。ここが唯一の呼び手になる。
 *
 * 読み直す契機は 2 つ。PowerPoint 側の選択変更と、画面からの明示の更新。図形名の変更は
 * 選択変更を伴うため、これで追いつく（選択ウィンドウでの改名は必ず選択が動く）。
 *
 * 走査が済んだ合図は {@link onReconciled} で外へ出す。設定の鍵を移すのはこの合図を受けた
 * 側の仕事で、ここからは呼ばない。`entities/generation-setting` が `features/office` を
 * 参照しているため、ここから設定ストアを呼ぶと循環参照になる。
 * 設計: docs/notes/設計ノート/2026-08-31 設計ノート アドイン 生成設定の識別子.md
 */

export type DeckState = {
    slides: ReconciledSlide[];
    /** 一度でも走査が終わったか。false のうちは画面が「読み込み中」を出す。 */
    loaded: boolean;
    /**
     * 直近の走査が失敗したか。**「読めなかった」と「本当に 0 枚」を分ける。**
     *
     * 両者を `slides: []` へ潰すと、画面は空のファイルを開いたときと同じ表示になり、しかも
     * 担当の紐づけ判定が材料ゼロで走って**全担当が「どの枚にも紐づいていない」に落ちる**。
     * 設定が壊れたように見えるが、実際は読み取りに失敗しただけでファイルの中身は無事である。
     */
    failed: boolean;
    /**
     * 直近の走査で、発行した識別子を**ファイルへ書けなかった**か。
     *
     * 読み取り専用で開いた pptx だとタグの書き込みだけが失敗する。読めた一覧は使えるので
     * 画面は出すが、設定の編集は鍵が保存されないまま進むことになるので、画面が断りを出す
     * ための旗である（記録 25）。読み取りそのものの失敗は failed が表す。
     */
    uidWriteFailed: boolean;
    /**
     * 走査が失敗した理由として、**Office の版が足りない**見込みが立つか。
     *
     * マニフェストが必要な API の版を宣言していないため、版の足りない環境でも警告なく読み込まれる
     * （記録 34）。そこでは選択の取得（PowerPointApi 1.5）が例外になり、失敗が「紙面を読み取れ
     * ませんでした」に潰れて原因が分からない。失敗したときだけ版を確かめ、画面が言い添える。
     */
    officeTooOld: boolean;
    /**
     * 直近の走査で識別子を**振り直した**件数（重複していた枚・枠の数）。
     *
     * 重複は先に現れた側を正とするので、複製をタスクペインを閉じたまま元より前へ動かすと、
     * 正が複製側になって元の枚・枠が白紙になる（記録 51）。どちらが元かを見分ける手がかりが
     * ファイルの中に無いため向きは直せない。**起きたことを画面が伝えるための件数**である。
     * 振り直しは次の走査では起きない（重複が解けている）ので、出るのは 1 回だけになる。
     */
    reissued: number;
};

const EMPTY: DeckState = {
    slides: [],
    loaded: false,
    failed: false,
    uidWriteFailed: false,
    officeTooOld: false,
    reissued: 0,
};

let state: DeckState = EMPTY;
let listeners = new Set<() => void>();
let reconciledListeners = new Set<(report: ReconcileReport) => void>();
let inflight: Promise<void> | null = null;
/** 走査中に来た要求ぶんの後追い。何本来ても 1 本に畳む（記録 71）。 */
let queued: Promise<void> | null = null;
let watching = false;

function emit(): void {
    listeners.forEach((l) => l());
}

/**
 * 走査して状態を差し替える。多重呼び出しは 1 本にまとめ、**走査中に来た要求は後追いで拾う。**
 *
 * 以前は走っている走査の約束をそのまま返して終わっていた。走っている走査は要求より前の紙面を
 * 読んでいるので、要求のきっかけになった変更は入っていない。走査は 3 往復あり、実測で 88 枚・
 * 1,170 図形の読みが 129ms（初回は識別子の書き込みが乗る）かかるため、図形を続けて消す・
 * 名前を続けて変えるといった操作では取りこぼしが起きた。画面の「読み直す」を押しても、その
 * とき走査中なら何も読み直されず、手応えも無かった（記録 71）。
 *
 * 後追いは何本要求が来ても 1 本にまとめる。返す約束は後追いまで終わってから解決するので、
 * `navigateToTarget` の「一度だけ読み直してから諦める」も意図どおりになる。
 *
 * 選択変更の側に間引きは置かない。後追いが 1 本に畳まれるため、続けて動かしても走査は
 * 高々 2 本に収まる。待ちを入れると、この画面の売りである選択への追従がそのぶん遅れる。
 */
export function refreshDeck(): Promise<void> {
    if (inflight) {
        // 走っている走査の結果で済ませない。終わってから 1 回だけ追いかける。
        if (!queued) {
            queued = inflight.then(() => {
                queued = null;
                return scan();
            });
        }
        return queued;
    }
    return scan();
}

function scan(): Promise<void> {
    inflight = reconcileUids()
        .then((report) => {
            state = {
                slides: report.slides,
                loaded: true,
                failed: false,
                uidWriteFailed: !report.uidsPersisted,
                officeTooOld: false,
                reissued: report.reissued,
            };
            // **書けていない鍵へ設定を移さない。** 移すとファイルの中に無い鍵を指した設定が
            // 残る（記録 25）。読めた一覧は上で state へ入れてあるので、画面は使える。
            if (report.uidsPersisted) reconciledListeners.forEach((l) => l(report));
            emit();
        })
        .catch(() => {
            // 読めなくても投げない。**失敗として描く**（0 枚の成功と混ぜない）。前回読めていた
            // 枚は捨てる: 古い一覧を残すと、いま開いているファイルの姿として読まれてしまう。
            // 識別子の移行は reconciledListeners を呼ばないので走らない（空の対応表で設定を
            // 消す事故は起きない）。
            // 版が足りないと選択の取得で落ちる。失敗したときだけ確かめる（毎回聞く必要は無い）。
            state = {
                slides: [],
                loaded: true,
                failed: true,
                uidWriteFailed: false,
                officeTooOld: !supportsRequiredApi(),
                reissued: 0,
            };
            emit();
        })
        .then(() => {
            inflight = null;
        });
    return inflight;
}

/**
 * 走査が終わるたびに呼ばれる。設定の鍵の移行はここに繋ぐ。
 * 返り値で購読を解除する。
 */
export function onReconciled(cb: (report: ReconcileReport) => void): () => void {
    reconciledListeners.add(cb);
    return () => {
        reconciledListeners.delete(cb);
    };
}

/**
 * 走査結果から、旧鍵→新鍵の対応表を作る。
 *
 * 重複していた creationId は**先に現れた図形へ寄せる**。後勝ちにすると、67 箇所が同じ値を
 * 名乗る本番データで設定が末尾の図形へ流れる。走査も先頭に元の識別子を残すので、向きが揃う。
 */
export function toUidMapping(report: ReconcileReport): {
    slides: { [officeSlideId: string]: string };
    elements: { [creationId: string]: string };
    names: { [uid: string]: string };
} {
    const slides: { [officeSlideId: string]: string } = {};
    const elements: { [creationId: string]: string } = {};
    // 表示名。v6 由来の担当は本文が対象を名指していないので、移行で書き起こすときに使う。
    const names: { [uid: string]: string } = {};
    for (const s of report.slides) {
        slides[s.officeSlideId] = s.uid;
        names[s.uid] = `スライド ${s.index}`;
        for (const sh of s.shapes) {
            if (sh.creationId && !(sh.creationId in elements)) elements[sh.creationId] = sh.uid;
            if (sh.name.trim().length > 0) names[sh.uid] = sh.name;
        }
    }
    return { slides, elements, names };
}

/**
 * 識別子から PowerPoint 上の在り処を引く。見つからなければ null。
 *
 * 走査が済んでいれば全枚ぶんの対応を持っているので、ここでの引き当てに往復は要らない。
 * 旧実装は creationId で全スライドを走査し、結果を settings へキャッシュしていたが、
 * 走査が 1 箇所に集まった以上どちらも不要になった。
 */
export function locate(uid: string): { officeSlideId: string; officeShapeId?: string } | null {
    const normalized = normalizeGuid(uid);
    for (const s of state.slides) {
        if (s.uid === uid) return { officeSlideId: s.officeSlideId };
        for (const sh of s.shapes) {
            // **アドインの識別子と `a16:creationId` の両方で引く。**
            //
            // サーバーの台帳に載る出力先は、v6・v7 のフォーマットでは図形の creationId を指す
            // 文字列（`{CC82D96B-…}` の形）である。アドインの識別子は `e-<36進の時刻>-<乱数>`
            // なので一致しようがなく、生成結果ペインから枠へ飛ぶ導線が必ず not-found になって
            // いた（記録 15）。走査結果は両方を持っているので、橋渡しの材料は手元にある。
            if (sh.uid === uid || (sh.creationId && normalizeGuid(sh.creationId) === normalized)) {
                return { officeSlideId: s.officeSlideId, officeShapeId: sh.officeShapeId };
            }
        }
    }
    return null;
}

/** GUID の表記ゆれ（波括弧・大文字小文字）を吸収する。creationId の照合に使う。 */
function normalizeGuid(value: string): string {
    return value
        .trim()
        .replace(/^\{|\}$/g, "")
        .toLowerCase();
}

export function isDeckLoaded(): boolean {
    return state.loaded;
}

export function getDeck(): DeckState {
    return state;
}

export function subscribeDeck(cb: () => void): () => void {
    listeners.add(cb);
    if (!watching) {
        watching = true;
        subscribeSelection(() => {
            void refreshDeck();
        });
    }
    if (!state.loaded && !inflight) void refreshDeck();
    return () => {
        listeners.delete(cb);
    };
}

/** テスト用。モジュール状態を初期へ戻す。 */
export function resetDeckForTest(): void {
    state = EMPTY;
    listeners = new Set();
    reconciledListeners = new Set();
    inflight = null;
    queued = null;
    watching = false;
}
